import React, { useState, useEffect } from 'react';
import { Modal } from 'bootstrap';
import { toast } from 'react-toastify';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { OverdueCollection } from '../../types/collections';
import { ToastContainer } from 'react-toastify';
// import './modal.css'; // Import the modal CSS

const getStatusBadgeClass = (status: string): string => {
  switch (status?.toLowerCase()) {
    case 'overdue':
      return 'bg-warning text-dark';
    case 'critical':
      return 'bg-danger';
    case 'recovery':
      return 'bg-danger bg-opacity-75';
    case 'partial':
      return 'bg-info';
    default:
      return 'bg-secondary';
  }
};

interface OverdueCollectionReminderModalProps {
  modalId?: string;
}

const OverdueCollectionReminderModal: React.FC<OverdueCollectionReminderModalProps> = () => {
  const [selectedCollection, setSelectedCollection] = useState<OverdueCollection | null>(null);
  const [reminderType, setReminderType] = useState<'sms' | 'email' | 'call'>('sms');
  const [reminderMessage, setReminderMessage] = useState('');
  const [reminderUrgency, setReminderUrgency] = useState<'normal' | 'urgent' | 'final'>('normal');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const modalElement = document.getElementById('OverdueCollectionReminder');
    if (!modalElement) return;

    // Show event handler
    const handleModalShow = (event: Event) => {
      const collectionDataString = (modalElement as HTMLElement).getAttribute('data-collection-data');
      if (collectionDataString) {
        try {
          const collectionData = JSON.parse(collectionDataString) as OverdueCollection;
          setSelectedCollection(collectionData);

          // Set default reminder message based on collection data
          const defaultMessage = `Dear ${collectionData.customerName}, URGENT: Your loan payment of ${formatCurrency(collectionData.dueAmount)} was due on ${formatDate(collectionData.dueDate)} and is now ${collectionData.daysOverdue} days overdue. Please make an immediate payment to avoid further penalties and legal action.`;
          setReminderMessage(defaultMessage);

          // Set default reminder urgency based on overdue days
          if (collectionData.daysOverdue > 40) {
            setReminderUrgency('final');
          } else if (collectionData.daysOverdue > 30) {
            setReminderUrgency('urgent');
          } else {
            setReminderUrgency('normal');
          }
        } catch (error) {
          console.error('Error parsing collection data:', error);
        }
      }
    };

    // Hidden event handler
    const handleModalHidden = (event: Event) => {
      modalElement.removeAttribute('data-collection-data');
      setSelectedCollection(null);
      setReminderMessage('');
      setReminderType('sms');
      setReminderUrgency('normal');
      setIsLoading(false);
    };

    // Attach event listeners
    modalElement.addEventListener('shown.bs.modal', handleModalShow);
    modalElement.addEventListener('hidden.bs.modal', handleModalHidden);

    // Cleanup
    return () => {
      modalElement.removeEventListener('shown.bs.modal', handleModalShow);
      modalElement.removeEventListener('hidden.bs.modal', handleModalHidden);
    };
  }, []);

  // Handle sending reminder
  const handleSendReminder = () => {
    if (!selectedCollection) return;

    setIsLoading(true);

    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);

      // Close the modal
      const modalElement = document.getElementById('OverdueCollectionReminder');
      if (modalElement) {
        const modalInstance = Modal.getInstance(modalElement);
        modalInstance?.hide();
      }

      toast.success(`${reminderType === 'call' ? 'Call reminder' : reminderType === 'sms' ? 'SMS' : 'Email'} reminder sent successfully to ${selectedCollection.customerName}`);
    }, 1500);
  };

  return (
    <div className="modal-content rounded-0">
      <div className="modal-header bg-danger text-white">
        <h5 className="modal-title">Send Overdue Payment Reminder</h5>
        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div className="modal-body custom_scroll">
        <form id="reminderForm">
          {/* Summary Section */}
          <div className="card mb-3">
            <div className="card-body p-3">
              <div className="row g-2">
                <div className="col-12">
                  <h6 className="card-title text-muted mb-2">Payment Status Summary</h6>
                </div>
                <div className="col-6">
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm me-3">
                      <div className="avatar-title bg-soft-danger text-danger rounded-circle">
                        <i className="fa fa-calendar-times"></i>
                      </div>
                    </div>
                    <div>
                      <h6 className="text-muted mb-0 small">Due Date</h6>
                      <div className="h6 fw-bold">{selectedCollection ? formatDate(selectedCollection.dueDate) : '-'}</div>
                    </div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm me-3">
                      <div className="avatar-title bg-soft-primary text-primary rounded-circle">
                        <i className="fa fa-coins"></i>
                      </div>
                    </div>
                    <div>
                      <h6 className="text-muted mb-0 small">Due Amount</h6>
                      <div className="h6 fw-bold">{selectedCollection ? formatCurrency(selectedCollection.dueAmount) : '-'}</div>
                    </div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm me-3">
                      <div className="avatar-title bg-soft-warning text-warning rounded-circle">
                        <i className="fa fa-clock"></i>
                      </div>
                    </div>
                    <div>
                      <h6 className="text-muted mb-0 small">Days Overdue</h6>
                      <div className="h6 fw-bold text-danger">{selectedCollection ? `${Math.abs(selectedCollection.daysOverdue)} days` : '-'}</div>
                    </div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm me-3">
                      <div className="avatar-title bg-soft-info text-info rounded-circle">
                        <i className="fa fa-tag"></i>
                      </div>
                    </div>
                    <div>
                      <h6 className="text-muted mb-0 small">Status</h6>
                      <div className="h6 fw-bold">
                        {selectedCollection ? (
                          <span className={`badge ${getStatusBadgeClass(selectedCollection.status)}`}>
                            {selectedCollection.status}
                          </span>
                        ) : '-'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Collection Details Section */}
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="card-title mb-3">Collection Details</h6>
              <div className="form-group mb-3">
                <label className="form-label">Customer Name</label>
                <input
                  type="text"
                  className="form-control"
                  id="customerName"
                  placeholder="Customer Name"
                  value={selectedCollection?.customerName || ''}
                  disabled
                />
              </div>
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label">Contact Number</label>
                    <input
                      type="text"
                      className="form-control"
                      value={selectedCollection?.contactNumber || ''}
                      disabled
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="text"
                      className="form-control"
                      value={selectedCollection?.email || ''}
                      disabled
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Reminder Settings Section */}
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="card-title mb-3">Reminder Settings</h6>

              <div className="form-group mb-3">
                <label className="form-label">Reminder Type <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  value={reminderType}
                  onChange={(e) => setReminderType(e.target.value as 'sms' | 'email' | 'call')}
                >
                  <option value="sms">SMS to {selectedCollection?.contactNumber}</option>
                  <option value="email">Email to {selectedCollection?.email}</option>
                  <option value="call">Phone Call</option>
                </select>
              </div>

              <div className="form-group mb-3">
                <label className="form-label">Urgency Level <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  value={reminderUrgency}
                  onChange={(e) => setReminderUrgency(e.target.value as 'normal' | 'urgent' | 'final')}
                >
                  <option value="normal">Normal</option>
                  <option value="urgent" className="text-warning">Urgent</option>
                  <option value="final" className="text-danger">Final Notice</option>
                </select>
              </div>

              <div className="form-group mb-3">
                <label className="form-label">Message <span className="text-danger">*</span></label>
                <textarea
                  className="form-control message-area"
                  id="reminderMessage"
                  placeholder="Message"
                  value={reminderMessage}
                  onChange={(e) => setReminderMessage(e.target.value)}
                ></textarea>
              </div>
            </div>
          </div>

          <div className="text-center">
            <button
              type="button"
              className="btn btn-lg bg-danger w-50 text-light"
              onClick={handleSendReminder}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Sending...
                </>
              ) : (
                'Send Reminder'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OverdueCollectionReminderModal;
